import axios from 'axios';
import {accessToken, clearLocalSession, isJwtAuthorizationError, refreshLocalSession} from './localSession';

const apiHost = process.env.VUE_APP_LOCAL_MODE === 'true' ? '' : (process.env.VUE_APP_APIURL || '');
let currentUser = null;
const sharedEventStreams = new Map();
const objectId = (value) => value || Array.from(crypto.getRandomValues(new Uint8Array(12))).map((item) => item.toString(16).padStart(2, '0')).join('');
function authToken() {
    const token = accessToken();
    if (token && !localStorage.getItem('token')) localStorage.setItem('token', token);
    return token;
}

function closeSharedEventStreams() {
    sharedEventStreams.forEach((stream) => {
        stream.source?.close();
        window.clearTimeout(stream.reconnectTimer);
        stream.source = null;
        stream.connecting = false;
    });
    sharedEventStreams.clear();
}

if (typeof window !== 'undefined') {
    window.addEventListener('storage', (event) => {
        if (!['token', 'updateToken', 'localRefreshToken', 'xcreateLocalRealmUser'].includes(event.key)) return;
        // localStorage is shared by tabs, while EventSource objects are not.
        // When another tab signs in as a different user, stop every stream in
        // this tab before it can reconnect with the newly written credentials.
        closeSharedEventStreams();
        currentUser = null;
    });
}

async function authorizedRequest(request, retry = true) {
    try {
        return await request(authToken());
    } catch (error) {
        if (!retry || !isJwtAuthorizationError(error)) throw error;
        const token = await refreshLocalSession();
        return request(token);
    }
}

function eventStream(databaseName) {
    if (sharedEventStreams.has(databaseName)) return sharedEventStreams.get(databaseName);
    const subscribers = new Set();
    const stream = {source: null, subscribers, connecting: false, reconnectTimer: null, openedOnce: false};
    sharedEventStreams.set(databaseName, stream);
    const connect = () => {
        if (stream.connecting || stream.source?.readyState === EventSource.OPEN) return;
        const token = authToken();
        if (!token) return;
        stream.connecting = true;
        authorizedRequest((activeToken) => axios.post(`${apiHost}/api/v1/local-events/token`, {database: databaseName}, {headers: {Authorization: `Bearer ${activeToken}`}})).then((response) => {
            if (!response.data.status) throw new Error(response.data.statusText || 'Realtime subscription denied');
            const source = new EventSource(`${apiHost}/api/v1/local-events?database=${encodeURIComponent(databaseName)}&token=${encodeURIComponent(response.data.data.token)}`);
            stream.source = source;
            source.onopen = () => {
                stream.connecting = false;
                // The first EventSource handshake matters just as much as a
                // reconnect. Consumers must not report "connected" before the
                // server can actually deliver changes, and every handshake
                // must trigger a durable-state catch-up for events that may
                // have happened while the transport was unavailable.
                subscribers.forEach((subscriber) => subscriber({operationType: 'reconnect', collection: '__connection__', fullDocument: null}));
                stream.openedOnce = true;
            };
            source.onmessage = (event) => {
                try {
                    const change = JSON.parse(event.data);
                    subscribers.forEach((subscriber) => subscriber(change));
                } catch (error) {
                    console.error('Invalid local realtime event', error);
                }
            };
            source.onerror = () => {
                source.close(); stream.source = null; stream.connecting = false;
                subscribers.forEach((subscriber) => subscriber({operationType: 'disconnect', collection: '__connection__', fullDocument: null}));
                window.clearTimeout(stream.reconnectTimer);
                stream.reconnectTimer = window.setTimeout(connect, 1000);
            };
        }).catch((error) => {
            stream.connecting = false;
            window.clearTimeout(stream.reconnectTimer);
            if (Number(error?.response?.status) === 403) {
                stream.source?.close();
                stream.source = null;
                sharedEventStreams.delete(databaseName);
                subscribers.forEach((subscriber) => subscriber({operationType: 'disconnect', collection: '__connection__', fullDocument: null}));
                return;
            }
            stream.reconnectTimer = window.setTimeout(connect, 1000);
            if (!isJwtAuthorizationError(error)) console.warn('Local realtime connection failed:', error?.message || 'unknown error');
        });
    };
    connect();
    return stream;
}

function collection(databaseName, name) {
    const call = async (method, args) => {
        const token = authToken();
        if (!token) throw new MongoDBRealmError('No authenticated local database session.');
        const response = await authorizedRequest((activeToken) => axios.post(`${apiHost}/api/v1/mongoOpration`, { dbName: databaseName, collection: name, methodName: method, dataObj: args }, {headers: {Authorization: `Bearer ${activeToken}`}}));
        if (!response.data.status) throw new Error(response.data.statusText || 'Local data operation failed');
        return response.data.statusText;
    };
    const watch = (options = {}) => {
        const queue = [];
        const waiters = [];
        // Global identity/account collections are intentionally excluded from
        // browser change streams by the server. Return a cancellable passive
        // iterator so legacy snapshot consumers keep their initial fetch but
        // do not generate forbidden subscription requests or retry loops.
        if (databaseName === 'global') {
            let closed = false;
            return {
                [Symbol.asyncIterator]() { return this; },
                next() {
                    if (closed) return Promise.resolve({done: true});
                    return new Promise((resolve) => waiters.push(resolve));
                },
                return() {
                    closed = true;
                    while (waiters.length) waiters.shift()({done: true});
                    return Promise.resolve({done: true});
                }
            };
        }
        const stream = eventStream(databaseName);
        const expectedId = options?.filter?.['fullDocument._id'];
        const receive = (change) => {
            if (!['reconnect', 'disconnect'].includes(change.operationType) && change.collection !== name) return;
            if (expectedId && String(change.fullDocument?._id) !== String(expectedId)) return;
            const waiter = waiters.shift();
            if (waiter) waiter({value: change, done: false}); else queue.push(change);
        };
        stream.subscribers.add(receive);
        // A database-level EventSource is shared by all collection watchers.
        // When a component mounts after that source is already open it will
        // not receive another native `onopen`, so acknowledge the established
        // transport to the new subscriber and let it perform its catch-up.
        if (stream.source?.readyState === EventSource.OPEN) {
            Promise.resolve().then(() => {
                if (stream.subscribers.has(receive)) receive({operationType: 'reconnect', collection: '__connection__', fullDocument: null});
            });
        }
        return {
            [Symbol.asyncIterator]() { return this; },
            next() {
                if (queue.length) return Promise.resolve({value: queue.shift(), done: false});
                return new Promise((resolve) => waiters.push(resolve));
            },
            return() {
                stream.subscribers.delete(receive);
                while (waiters.length) waiters.shift()({done: true});
                return Promise.resolve({done: true});
            }
        };
    };
    return new Proxy({}, { get: (_, method) => method === 'watch' ? watch : (...args) => call(method, args) });
}

function makeUser(data) {
    return {
        id: data.id,
        profile: data.profile || {},
        _profile: { data: data.profile || {} },
        mongoClient: () => ({ db: (name) => ({ collection: (collectionName) => collection(name, collectionName) }) }),
        logOut: async () => {
            closeSharedEventStreams();
            currentUser = null;
            clearLocalSession();
        }
    };
}

function restoreUser() {
    if (currentUser && !localStorage.getItem('xcreateLocalRealmUser')) currentUser = null;
    if (currentUser) return currentUser;
    try {
        const stored = JSON.parse(localStorage.getItem('xcreateLocalRealmUser') || 'null');
        if (stored?.id) currentUser = makeUser(stored);
    } catch (_) {
        clearLocalSession();
    }
    return currentUser;
}

export class App {
    constructor() { this.id = 'local-development'; }
    get currentUser() { return restoreUser(); }
    async logIn(credentials) {
        const response = await axios.post(`${apiHost}/api/v1/local-auth/login`, credentials);
        if (!response.data.status) throw new MongoDBRealmError(response.data.statusText || 'Invalid email or password');
        // A browser can switch accounts without first visiting the explicit
        // logout route. Old company EventSources must never reconnect with the
        // new account token or they will create an authorization-error loop.
        closeSharedEventStreams();
        if (response.data.token) {
            localStorage.setItem('token', response.data.token);
            localStorage.setItem('updateToken', response.data.token);
        }
        if (response.data.refreshToken) localStorage.setItem('localRefreshToken', response.data.refreshToken);
        currentUser = makeUser(response.data.user);
        localStorage.setItem('xcreateLocalRealmUser', JSON.stringify({id: response.data.user.id, profile: response.data.user.profile || {}}));
        return currentUser;
    }
}
export const Credentials = { emailPassword: (email, password) => ({ email, password }) };
export class MongoDBRealmError extends Error {}
export const BSON = { ObjectId: objectId, ObjectID: objectId };
